import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import {
  CHARGE_TYPES,
  Initiative,
  STRATEGIES,
} from '@totem/components/portfolio/buildings/details/optimize/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  row: { display: 'flex', gap: '4%' },
  halfWidth: { paddingBottom: '0', marginBottom: '1rem', width: '48%' },
  formItem: { paddingBottom: '0', marginBottom: '1rem', width: '100%' },
};

type FormState = {
  name: string;
  description: string;
  strategy: string;
  chargeType: string;
  effectiveDate: Dayjs | null;
  notes: string;
};

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  strategy: '',
  chargeType: 'energy',
  effectiveDate: null,
  notes: '',
});

type Props = {
  open: boolean;
  buildingId: string;
  programId: string;
  initiative: Initiative | null;
  onClose: () => void;
  onSaved: () => void;
};

const InitiativeModal = ({
  open,
  buildingId,
  programId,
  initiative,
  onClose,
  onSaved,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const isEditing = !!initiative;
  const isValid =
    form.name.trim() !== '' &&
    form.strategy !== '' &&
    form.effectiveDate !== null;

  useEffect(() => {
    if (open) {
      if (initiative) {
        setForm({
          name: initiative.name,
          description: initiative.description ?? '',
          strategy: initiative.strategy,
          chargeType: initiative.chargeType,
          effectiveDate: initiative.effectiveDate
            ? dayjs(initiative.effectiveDate)
            : null,
          notes: initiative.notes ?? '',
        });
      } else {
        setForm(emptyForm());
      }
    }
  }, [open, initiative]);

  const handleSubmit = useCallback(() => {
    if (isSending || !isValid) {
      return;
    }
    setIsSending(true);

    const url = isEditing
      ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives/${initiative.id}`
      : `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives`;
    const method = isEditing ? 'PATCH' : 'POST';

    const payload = {
      ...form,
      programId,
      effectiveDate: form.effectiveDate
        ? form.effectiveDate.toISOString()
        : null,
    };

    fetch(url, {
      method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => {
        CheckResponseShowError(res);

        onSaved();

        onClose();
      })
      .finally(() => setIsSending(false));
  }, [
    form,
    isSending,
    isEditing,
    isValid,
    buildingId,
    programId,
    initiative,
    getApiToken,
    onSaved,
    onClose,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      title={
        <ModalTitle>
          {isEditing ? 'Edit Initiative' : 'Add Initiative'}
        </ModalTitle>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSending}
            disabled={!isValid}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Name" required style={styles.formItem}>
          <Input
            value={form.name}
            onChange={(evt) => setForm({ ...form, name: evt.target.value })}
            placeholder="e.g. HVAC Schedule Optimization"
          />
        </Form.Item>

        <Form.Item label="Description" style={styles.formItem}>
          <Input.TextArea
            rows={2}
            value={form.description}
            onChange={(evt) =>
              setForm({ ...form, description: evt.target.value })
            }
            placeholder="Optional"
          />
        </Form.Item>

        <div style={styles.row}>
          <Form.Item label="Strategy" required style={styles.halfWidth}>
            <Select
              value={form.strategy || undefined}
              onChange={(val) => setForm({ ...form, strategy: val })}
              options={STRATEGIES}
              placeholder="Select strategy"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Charge Type" required style={styles.halfWidth}>
            <Select
              value={form.chargeType}
              onChange={(val) => setForm({ ...form, chargeType: val })}
              options={CHARGE_TYPES}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>

        <Form.Item label="Effective Date" required style={styles.formItem}>
          <DatePicker
            style={{ width: '100%' }}
            value={form.effectiveDate}
            onChange={(val) => setForm({ ...form, effectiveDate: val })}
          />
        </Form.Item>

        <Form.Item label="Notes" style={styles.formItem}>
          <Input.TextArea
            rows={2}
            value={form.notes}
            onChange={(evt) => setForm({ ...form, notes: evt.target.value })}
            placeholder="Optional"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InitiativeModal;
